import { SeriesRequest } from "./dashboard-chart";
import { isPrimaryKey, isTemporal, type Field } from "./schema-field";

/**
 * A two-point line is a diagonal. It carries no shape: every pair of numbers
 * becomes one falling or rising segment, which is how a 2-row table was
 * reading as a trend. Three months is the first series that can bend.
 */
export const MIN_SPARKLINE_POINTS = 3;

/**
 * The request a demo tile sends for its sparkline.
 *
 * `seriesRequestFromSchema(undefined, schema)` never matches
 * `/line|area|time|trend/`, so it takes the categorical branch: COUNT per
 * status (or whatever enum comes first), which the API returns sorted. Drawn
 * as a line that series can only descend, and eight falling tiles read as a
 * product whose every table is shrinking.
 *
 * Tiles ask for a month bucket on the first temporal column and omit the
 * measure, so the values are COUNT(*) over time — the same quantity the tile
 * already prints as a record count. A SUM of Budget would be a different
 * story, and would reintroduce the "caption vs plot" contradiction the
 * collection charts already had to unpick.
 *
 * No temporal column → no request → no sparkline. A categorical count is not
 * a trend and must not be drawn as one.
 */
export function sparklineRequestFromSchema(
  schema: Field[],
): SeriesRequest | null {
  const temporal = schema
    .filter((field) => !isPrimaryKey(field))
    .find(isTemporal);
  if (!temporal) return null;
  return {
    groupBy: temporal.name,
    bucket: "month",
    type: "line",
  };
}

/**
 * Chronological values for a month-bucketed series.
 *
 * Sort by the bucket label, never by value: value-order is what made a
 * categorical count look like a collapse.
 */
export function sparklineValues(
  points: Array<{ label: string; value: number }> | undefined,
): number[] {
  return [...(points ?? [])]
    .filter((point) => Number.isFinite(point.value))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)))
    .map((point) => point.value);
}

export type TileCollection = {
  id?: string;
  name: string;
  fields?: unknown[];
};

export type TileSeries<T extends TileCollection = TileCollection> = {
  collection: T;
  rows?: number;
  values: number[];
};

/**
 * Ranked collections that can actually draw a trend, then the rest, capped
 * so we do not fire 43 aggregates to pick four tiles.
 */
export function tileCandidates<T extends TileCollection>(
  collections: T[],
  pool = 12,
): T[] {
  const withTime: T[] = [];
  const without: T[] = [];
  for (const collection of collections) {
    const schema = (collection.fields ?? []) as unknown as Field[];
    if (sparklineRequestFromSchema(schema)) withTime.push(collection);
    else without.push(collection);
  }
  return [...withTime, ...without].slice(0, pool);
}

/**
 * Featured tiles: skip empty tables, prefer ones whose series can bend.
 *
 * Order inside each group is the caller's (ranked) order, so a populated
 * Projects table still beats a populated join table.
 */
export function pickFeaturedTiles<T extends TileSeries>(
  loaded: T[],
  featured = 4,
): T[] {
  const withRows = loaded.filter((tile) => (tile.rows ?? 0) > 0);
  const withSpark = withRows.filter(
    (tile) => tile.values.length >= MIN_SPARKLINE_POINTS,
  );
  const rest = withRows.filter(
    (tile) => tile.values.length < MIN_SPARKLINE_POINTS,
  );
  return [...withSpark, ...rest].slice(0, featured);
}
